import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import Parser from 'react-html-parser'
import { HighlightItem } from '../../../../model/caf.interface'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useHtmlParserTransform from '../../../../modules/hooks/webview/caf/common/useHtmlParserTransform'
import useMapTagToWording from '../../../../modules/hooks/webview/caf/common/useMapTagToWording'
import useWebviewNavigation from '../../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { IC_CAF_ALERT_TRIANGLE, IC_CAF_ERROR_CIRCLE } from '../../../../modules/images'
import urls from '../../../../modules/urls'
import { COLORS } from '../../../../modules/vars'
import { LESSON_ACTIVITIES_MAP, useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import SimpleLineChart from '../charts/SimpleLineChart'
import ArrowLinkBox from '../common/ArrowLinkBox'
import HighlightedTitle from '../common/HighlightedTitle'

const highlightsContent = {
  13: (detail: HighlightItem['details']) => <RepetitionDetail detail={detail} />,
  14: (detail: HighlightItem['details']) => <SentenceDetail detail={detail} type="warning" />,
  15: (detail: HighlightItem['details']) => <SpeechDetail detail={detail} />,
  16: (detail: HighlightItem['details']) => <FillerWordDetail detail={detail} />,
  17: (detail: HighlightItem['details']) => <SentenceStructureDetail detail={detail} />,
  18: (detail: HighlightItem['details']) => <RepetitionDetail detail={detail} />,
  10: (detail: HighlightItem['details']) => <SentenceDetail detail={detail} />,
}

type HighlightType = 'pinned' | 'list' | 'recommended' | ''
const SimpleHighlight = ({
  highlightItem,
  list = false,
  type = '',
}: {
  highlightItem: HighlightItem
  list?: boolean
  type?: HighlightType
}) => {
  const { t } = useTranslation()
  const { navigate } = useWebviewNavigation()
  const isMobile = useIsMobile()
  const router = useAppRouter()
  const isWebview = useIsWebview()
  const { useLessonsFlow } = useLessonsStack()
  const { push } = useLessonsFlow()
  const handleClickHighlightItem = (highlightId, tag) => () => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIHighlightDetailActivity, { highlightId, tag })
    } else if (isMobile) {
      const url = urls.webview.caf.highlight.detail(highlightId, tag)
      navigate({ url })
    } else {
      router.push(
        urls.student.newPortal.lessons.reviewDetail.asCafTagAndId(router.query?.lessonId, 5, tag, highlightId)
      )
    }
  }
  return (
    <ArrowLinkBox
      key={highlightItem.metric + '_highlight'}
      title={<LinkTitle content={highlightItem.highlight_tag} />}
      subtitle={
        <HighlightedTitle
          title={
            highlightItem.active
              ? t(n_caf.highlight.error[`grammar${highlightItem.highlight_tag}`](highlightItem.count))
              : t(n_caf.highlight.correct[`grammar${highlightItem.highlight_tag}`])
          }
          color={COLORS.PURPLE50}
        />
      }
      subText={highlightItem.component}
      onClick={handleClickHighlightItem(highlightItem.id, highlightItem.highlight_tag)}
      active={highlightItem.active}
    >
      {!list && highlightItem.active && (
        <LinkContent tag={highlightItem.highlight_tag} detail={highlightItem.details} />
      )}
    </ArrowLinkBox>
  )
}

const LinkTitle = ({ content }: { content: number }) => {
  const { t } = useTranslation()
  const { namePerTag, iconPerTag } = useMapTagToWording()
  const Icon = iconPerTag(content)

  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <div className="mb-4 flex items-center">
        <Icon width={18} height={18} className="mr-[2px]" color={COLORS.PURPLE500} />
        <span
          className={classNames('text-h5 h-[18px] font-medium', {
            'text-purple-500': true,
          })}
        >
          {t(namePerTag(content))}
        </span>
      </div>
    )
  } else {
    return (
      <div
        className={classNames('mb-4 flex items-center', {
          'color-purple-500': true,
        })}
      >
        <Icon
          width={18}
          height={18}
          className="mr-[2px] [&>*]:fill-current"
          color={COLORS.PURPLE500}
          fill={COLORS.PURPLE500}
        />
        <span
          className={classNames('text-h5 h-[18px] font-medium', {
            'text-purple-500': true,
          })}
        >
          {t(namePerTag(content))}
        </span>
      </div>
    )
  }
}

const LinkContent = ({ tag, detail }: { tag: number; detail: HighlightItem['details'] }) => {
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <div className="mt-12 pt-6">
        {highlightsContent[tag] ? highlightsContent[tag](detail) : highlightsContent[10](detail)}
      </div>
    )
  } else {
    return <div>{highlightsContent[tag] ? highlightsContent[tag](detail) : highlightsContent[10](detail)}</div>
  }
}

const RepetitionDetail = ({ detail }: { detail: HighlightItem['details'] }) => {
  return (
    <div className="flex max-h-[74px] flex-wrap gap-10 overflow-hidden">
      {detail.slice(0, 5).map((item) => (
        <div key={item} className="rounded-[20px] bg-gray-50 py-4 px-12">
          <span className="text-h4 text-purple-500">{item}</span>
        </div>
      ))}
    </div>
  )
}

const SentenceDetail = ({ type = 'error', detail }) => {
  console.log(detail, 'detaildetaildetaildetail')
  const { transformHTMLNode } = useHtmlParserTransform()
  return (
    <div className="flex items-start gap-10 rounded-[6px] bg-gray-50 p-14">
      <img src={type === 'error' ? IC_CAF_ERROR_CIRCLE : IC_CAF_ALERT_TRIANGLE} width={16} height={16} alt="alert" />
      <span className="line-clamp-3 text-[14px] tracking-tight text-black">
        {type === 'error'
          ? Parser(detail.my_sentence, {
              transform: transformHTMLNode,
            })
          : Parser(detail?.sentence, {
              transform: transformHTMLNode,
            })}
      </span>
    </div>
  )
}

const FillerWordDetail = ({ detail }: { detail: HighlightItem['details'] }) => {
  const { transformHTMLNode } = useHtmlParserTransform()

  return (
    <div className="flex items-start gap-10 rounded-[6px] bg-gray-50 p-14">
      <span className="line-clamp-3 text-[14px] tracking-tight text-black">
        {Parser(detail.sentence, {
          transform: transformHTMLNode,
        })}
      </span>
    </div>
  )
}
const SentenceStructureDetail = ({ detail }: { detail: HighlightItem['details'] }) => {
  const { transformHTMLNode } = useHtmlParserTransform()

  return (
    <div className="flex items-start gap-10 rounded-[6px] bg-gray-50 p-[12px]">
      <span className="line-clamp-3 text-[14px] tracking-tight text-black">
        {Parser(detail.sentence, {
          transform: transformHTMLNode,
        })}
      </span>
    </div>
  )
}

const SpeechDetail = ({ detail }: { detail: HighlightItem['details'] }) => {
  const chartWrapRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [chartWidth, setChartWidth] = useState(0)
  const [chartHeight, setChartHeight] = useState(0)

  useEffect(() => {
    if (chartWrapRef.current) {
      const width = chartWrapRef.current.offsetWidth
      const height = chartWrapRef.current.offsetHeight
      setChartWidth(width)
      setChartHeight(height)
    }
  }, [chartWrapRef])
  if (isMobile) {
    return (
      <div className="flex h-[52px] justify-between overflow-hidden">
        <div className="text-14 flex flex-shrink-0 items-end font-bold text-gray-300">
          {t(n_caf.highlight.fraction_counts(detail?.detail_count, detail?.total_count))}
        </div>
        <div className="flex-1 items-center justify-end">
          <div className={classNames('h-[52px] w-[100%] max-w-[216px] flex-1')} ref={chartWrapRef}>
            <SimpleLineChart
              width={chartWidth}
              height={chartHeight}
              data={detail.graph_data}
              dataKey="wpm"
              indexBy="sentence"
            />
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <div className="flex h-[100%] justify-between overflow-hidden">
        <div className="text-14 mr-[30px] flex flex-shrink-0 items-end font-bold text-gray-300 sm:pr-[36px] lg:pr-[100px]">
          {t(n_caf.highlight.fraction_counts(detail?.detail_count, detail?.total_count))}
        </div>
        <div
          className={classNames('flex h-[90px] w-[100%] flex-1 flex-row items-center justify-end')}
          ref={chartWrapRef}
        >
          <SimpleLineChart
            width={chartWidth}
            height={chartHeight}
            data={detail.graph_data}
            dataKey="wpm"
            indexBy="sentence"
          />
        </div>
      </div>
    )
  }
}

export default SimpleHighlight
